<template>
  <div class="card-list-container safe-top-padding">

    <transition name="fade">
      <grid-loader v-if="loading" full/>
      <card-failed
        class="relative-index"
        v-if="!loading && addCardFailed"
        :error="error"
        @close="addCardFailed = false"/>
    </transition>

    <transition name="fade">
      <div class="mask" @click="newCard = false" v-if="newCard"></div>
    </transition>

    <div class="cc-list-header">
      <icon-button 
        icon="el-icon-close"
        class="new-card-close show-mobile" /> 
      {{ title || 'Credit cards' }}
      <icon-button
        icon="el-icon-circle-plus-outline"
        @click="newCard = true">
          Use another card
      </icon-button>
    </div>

    <div class="card-list" :style="{ height: newCard ? 'calc(100% - 360px)' : '100%' }">
      <credit-card
        v-for="card in cards"
        :key="card.id"
        :card="card"
        :selectedId="selectedId"
        :editable="manage"
        @remove="httpRemoveCard"
        @select="setSelectedId" />
    </div>

    <div class="new-card" :class="newCardClassList">
      <div class="new-card-container relative">
        <icon-button
          v-if="!manage"
          icon="el-icon-close"
          class="new-card-close"
          @click.native="newCard ? newCard = false : $emit('close')" />
        <h1>Card details</h1>
        <stripe-form
          @success="retrieveToken" 
          @failed="retrieveTokenFailed"
          :getToken="getToken"
          class="top-margin"/>
        <el-checkbox
          v-model="saveCard"
          v-if="!requiresSave && !manage"
          style="margin-top: 30px;">
            Save card for later use
          </el-checkbox>
        <el-button
          :loading="getToken"
          type="primary"
          @click="getToken = true, loading = true">
            {{ isCharge ? 'Proceed' : 'Add card' }}
        </el-button>
      </div>
    </div>


    <el-button
      type="primary" 
      class="action-button"
      v-if="cards.length > 0 || manage"
      @click="handleAction">
        {{ buttonTitle || 'Proceed' }}
    </el-button>

  </div>
</template>

<script>
import CardBg from '@/assets/credit-card-bg.svg'
import CardEmptyIcon from '@/assets/credit-cards-payment.svg'
import CreditCard from './CreditCard'
import StripeForm from './stripe/Stripe'
import GridLoader from '@/components/shared/loaders/GridLoader'
import CardFailed from './payment-state/Failed'

export default {
  name: 'CardList',
  components: { CreditCard, StripeForm, GridLoader, CardFailed },
  props: {
    organization: {
      type: Object,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    showLoading: {
      type: Boolean,
      required: false
    },
    manage: {
      type: Boolean,
      required: false
    },
    selected: {
      type: String,
      required: false
    },
    requiresSave: {
      type: Boolean,
      required: false
    },
    buttonTitle: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      getToken: false,
      loading: this.showLoading || false,
      loaded: false,
      cardBg: CardBg,
      cardEmptyIcon: CardEmptyIcon,
      cards: [],
      selectedId: '',
      newCard: false,
      saveCard: (this.manage || this.requiresSave) ? true : false,
      token: null,
      addCardFailed: false,
      error: ''
    }
  },
  computed: {
    apiQuery () {
      return this.organization ? '?orgId=' + this.organization.id : ''
    },
    newCardClassList () {
      return {
        show: this.newCard,
        noCard: (this.cards.length === 0 && this.loaded && !this.manage),
        'noCard-w-button': (this.cards.length === 0 && this.loaded && this.manage)
      }
    },
    isCharge () {
      return !this.requiresSave && !this.manage
    },
    cardObject () {
      return {
        brand: this.token.card.brand,
        expirationMonth: this.token.card.exp_month,
        expirationYear: this.token.card.exp_year,
        last4: this.token.card.last4,
        token: this.token.card.id
      }
    }
  },
  methods: {
    retrieveTokenFailed (token) {
      this.getToken = false
      this.$message.error('Could not proceed')
    },
    retrieveToken (token) {
      this.token = token.token
      if (this.saveCard) {
        this.httpSaveCard()
      } else {
        if (this.isCharge) {
          this.handleAction()
          return
        }
        this.loading = false
      }
      this.newCard = false
      this.getToken = false
    },
    handleAction () {
      if (this.manage) {
        this.$emit('close')
      } else {
        this.$emit('proceed', {
          id: this.selectedId,
          token: this.token
        })
      }
    },
    async httpRemoveCard (card) {
      try {
        const response = await this.$http.delete(this.$api.payments.cards.remove(card.id) + this.apiQuery)
        var index = this.cards.indexOf(card)
        this.cards.splice(index, 1)
        this.$message('Credit card removed')  
      } catch (e) {
        this.$message('Could not remove card')
      }
    },
    httpSaveCardSuccess (response) {
      this.token = null
      this.selectedId = response.data.id
      if (this.isCharge) {
        this.handleAction()
      } else {
        this.$message.success('Credit card added')
        this.cards.push(response.data)
        this.loading = false
      }
    },
    async httpSaveCard () {
      try {
        const response = await this.$http.post(this.$api.payments.cards.save(this.token.id) + this.apiQuery.replace('?', '&'), this.cardObject)
        this.httpSaveCardSuccess(response)
      } catch (e) {
        this.error = e.response.data['']
        this.addCardFailed = true
      }
      this.loading = false
    },
    setSelectedId (id) {
      this.selectedId = id
      this.$emit('select', id)
    }
  },
  created () {
    this.$http.get(this.$api.payments.cards.get + this.apiQuery)
      .then(response => {
        this.cards = response.data
        if (this.selected) {
          this.selectedId = this.selected
        } else {
          this.selectedId = this.cards.length > 0 ? this.cards[0].id : ''
        }
        this.loading = false
        this.loaded = true
        this.$emit('load')
      }, _ => {
        this.$emit('load')
      })
  }
}
</script>

<style lang="scss" scoped>

  .new-card-close {
    position: absolute;
    right: 40px;
    top: 30px;
  }

  .top-margin {
    margin-top: 60px;
  }

  .relative-index {
    background-color: white;
    position: relative;
    z-index: 99999991;
  }

  .mask {
    background-color: rgba(black, 0.5); 
    width: 100%;
    position: absolute;
    height: 100vh;
    top: 0px;
    z-index: 9999998;
  }

  .new-card {
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    align-items: flex-start;
    position: absolute;
    width: 100%;
    height: 230px;
    background-color: $bg;
    z-index: 9999999;
    bottom: -350px;
    transition: bottom .3s ease-out;

    &.show {
      bottom: 0px;

      @include breakpoint (xs) {
        //bottom: 50px;
      }
    }

    .new-card-container {
      padding: 20px 40px;
      width: 100%;
      box-shadow: 0px -2px 8px 0px rgba(0, 0, 0, 0.025);
      background-color: white;
    }

    &.noCard-w-button {
      bottom: 60px;
      height: calc(100vh - 60px);

      .new-card-container {
        height: 100% !important;
      }
    }

    &.noCard {
      bottom: 0px;
      height: 100vh;

      .new-card-container {
        height: 100% !important;
      }
    }

    button {
      width: 100%;
      margin-top: 40px;
    }

    h1 {
      margin-bottom: 0px;
      font-size: 12px;
      color: $text-light;
      font-weight: 600;
      text-transform: uppercase;
    }
  }

  .action-button {
    position: absolute;
    bottom: 0px;
    width: 100%;
    height: 60px;
    right: 0;
    border-radius: 0;
    z-index: 9999998;

    @include breakpoint (xs) {
      //bottom: 50px;
    }
  }
  .cc-list-header {
    background-color: transparent;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 25px 40px;
    font-size: 12px;
    font-weight: 600;
    color: $text-light;
    text-transform: uppercase;

    @include breakpoint (xs) {
      padding: 70px 40px 25px 40px;
    }
  }

  .card-list-container {
    position: relative;
    height: 100vh;
    background-color: $bg;
  }

  .card-list {
    overflow-y: scroll !important;
    width: 100%;
    position: relative;
    box-sizing: border-box;
    padding: 0px 40px;
    margin-bottom: 80px;
    transition: height .25s ease-out;

    &::-webkit-scrollbar { 
      display: none; 
    }

    @include breakpoint (xs) {
      height: calc(100% - 200px) !important;
    }
  }

  .new-card-button {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/saldoAndCredit/CardList.vue